import React from 'react';
import { Address, Checkboxes, EmailAddressValidator, PhoneNumberValidator, Radio, RequiredValidator, Select, SwitchGroup, TextArea, TextField } from '../../../Components/Form';
import Accordion, {Panel} from '../../../Components/Containers/Accordion';

const TermsOfService = ({templateOptions}) => {
    return <Accordion>
        <Panel title="Contact details">

            <TextField name="businessLegalName"
                label="Business Legal Name"
                validators={[RequiredValidator]}
            />

            <Address name="contactAddress"
                label="Business Address"
                validators={[RequiredValidator]}
            />

            <TextField name="contactEmail"
                label="Contact Email"
                type="email"
                validators={[RequiredValidator, EmailAddressValidator]}
            />

            <TextField name="contactPhone"
                label="Contact phone number"
                type="tel"
                validators={[RequiredValidator, PhoneNumberValidator]}
            />
        </Panel>

        <Panel title="Service guarantees">
            <Radio name="serviceGuaranteeCompletion"
                label="Service completion timelines"
                validators={[RequiredValidator]}
                options={[
                    'We guarantee to complete services by the agreed-upon deadline, except in cases of circumstances beyond our control.',
                    'We make our best effort to complete services on time but cannot guarantee due to external factors.',
                    'Service completion is subject to change based on scheduling availability and external conditions.',
                    'No service completion guarantee should be included in the agreement.',
                ]}
            />

            <Radio name="serviceGuaranteeUnavoidableDelays"
                label="In the event of unavoidable delays"
                validators={[RequiredValidator]}
                options={[
                    'We will notify the client at the earliest opportunity and reschedule as soon as possible.',
                    'We will make our best effort to complete the service as soon as possible, but for repeating services we may have to miss the current cycle.',
                ]}
            />

            <TextField name="serviceGuaranteeAdditionalDetails"
                label="Service guarantee additional details"
                validators={[]}
            />
        </Panel>

        <Panel title="Payment terms">

            <Select name="paymentTerms"
                label="Payment terms"
                unselected=" "
                options={[
                    'Payment due upon receipt',
                    'Payment due within 7 days',
                    'Payment due within 14 days',
                    'Payment due within 30 days',
                    'Payment due within 60 days',
                    'Payment due within 90 days',
                ]}
                validators={[RequiredValidator]}
            />

            <Select name="paymentEscalation"
                label="Payment escalation"
                unselected=" "
                options={[
                    'No late payment fees or interest',
                    'Late payment fees',
                    'Interest on late payments',
                    'Interest on late payments and late payment fees',
                ]}
                help={
                    v => (v && v !== 'No late payment fees or interest') ?
                        'Please specify interest or fees in the additional details field'
                        : null
                    }
                validators={[RequiredValidator]}
            />

            <Select name="paymentRecovery"
                label="Payment recovery"
                unselected=" "
                options={[
                    'No further action',
                    'We may sell your debt to a debt collector',
                    'We may take legal action to recover the debt',
                ]}
                validators={[RequiredValidator]}
            />

            <TextField name="paymentAdditionalDetails"
                label="Payment additional details"
                validators={[]}
            />
        </Panel>

        <Panel title="Customer responsibilities">

            <SwitchGroup name="customerResponsibilities"
                label="Customer responsibilities"
                switches={[
                    'Provide a safe working environment',
                    'Provide access to the property during the agreed upon times',
                    'Provide access to utilities as required and agreed upon',
                    'Notify the business of any changes to the service requirements within a reasonable time frame',
                ].map(s => ({name: s, label: s}))}
            />

            <SwitchGroup name="customerResponsibilitiesConsequences"
                label="If the customer does not meet their responsibilities"
                switches={[
                    'The services may not be completed',
                    'The customer may still be charged for the services not delivered',
                    'The agreement may be terminated without refund or notice',
                ].map(s => ({name: s, label: s}))}
            />

            <TextField name="customerResponsibilitiesAdditionalDetails" label="Customer responsibilities additional details" />
        </Panel>

        <Panel title="Cancellation">

            <Radio name="customerCancellation"
                label="If the customer wishes to cancel"
                options={[
                    'Don\'t include cancellation terms in the agreement',
                    'The customer may cancel at any time',
                    'The customer may cancel with a notice period',
                    'The customer may cancel with a notice period and a cancellation fee',
                ]}
                help={v => (v && v.indexOf('notice') > 0) ? 'Please specify the notice period and fee in the additional details field' : null}
            />

            <TextArea name="customerCancellationDetails" label="Customer cancelation additional details" />

            <Checkboxes name="businessCancellation"
                label="Business cancellation"
                options={[
                    'We may cancel the agreement at any time',
                    'We may cancel the agreement without notice or reason',
                ]}
            />

            <TextArea name="businessCancellationDetails" label="Business cancelation additional details" />
        </Panel>

        <Panel title="Refund policy">

            <Radio name="refundPolicy"
                label="Refund policy"
                options={[
                    'No Refunds are provided',
                    'Repeat Service - No monetary refunds, but we will repeat the service if you are not satisfied',
                    'Full Refund - Full money back guarantee within a reasonable period',
                    'Partial Refund - Partial money back under certain conditions',
                    'Other'
                ]}
                validators={[RequiredValidator]}
                help={(v) => v === 'Other' ? 'Describe the refund policy in the additional details field' : null}
            />

            <TextField name="refundPolicyAdditionalDetails" label="Refund policy additional details" />
        </Panel>

        <Panel title="Dispute resolution">

            <Select name="disputeInitiation"
                label="How should disputes be initiated?"
                unselected=" "
                options={[
                    'Speak to a manager in person',
                    'Call our phone number',
                    'Email us',
                    'In writing to our postal address',
                ]}
                validators={[RequiredValidator]}
            />

            <Select name="disputeEscalation"
                label="How should disputes be escalated?"
                options={[
                    'Don\'t include an escalation process',
                    'Arbitration by a mutually agreed upon arbitrator.',
                    'Arbitration by a arbitrator selected by the business.',
                    'Litigation in a court of competent jurisdiction.',
                    'Resolution through an industry-specific regulatory body.',
                ]}
                validators={[RequiredValidator]}
            />

            <TextField name="disputeResolutionAdditionalDetails" label="Dispute resolution additional details" />
        </Panel>

        <Panel title="Additional details">
            <Checkboxes name="otherDocuments"
                label="Supplemental documents"
                help="The agreement may be supplemented by other documents"
                options={[
                    'Service specific terms and conditions',
                    'Individually negotiated terms',
                ]}
            />

            <Checkboxes name="revisionPolicy"
                label="Amendment and revision policy"
                options={[
                    'We may revise the document at any time',
                ]}
            />

            <TextArea name="additionalInformation"
                label="Additional details to include"
                help="You may include any additional details here which should be included in the document"
            />
        </Panel>
    </Accordion>
}

export default TermsOfService;
